import type React from "react";
import { css } from "@linaria/core";
import {
  Fragment,
  type ComponentProps,
  type ComponentType,
  type ReactNode,
} from "react";
import clsx from "clsx";
import { CLASS_CONSTANTS } from "@/utils/constants";
import { RenderHtml } from "./RenderHtml";
import { getRenderer } from "./component.store";

export type IFlexProps<T extends keyof React.JSX.IntrinsicElements = "div"> =
  ComponentProps<T> & {
    as?: T | React.JSXElementConstructor<unknown & Record<"className", string>>;
    titleAs?: string | ComponentProps<React.JSXElementConstructor<object>>;
    reverse?: boolean;
    isInline?: boolean;
    title?: ReactNode;
    description?: ReactNode;
    showTitle?: boolean;
    showDescription?: boolean;
    showChildrenBeforeDescription?: number;
    titleClassName?: string;
    descriptionClassName?: string;
    flex?: number | string | undefined;
  };

const getDirection = (direction: string, reverse: boolean) => {
  return direction + (reverse ? "-reverse" : "");
};

const Title = getRenderer(
  "Title",
  (
    props: ComponentProps<"div"> & {
      label?: string;
      showTitle?: boolean;
      as?: string;
    },
  ) => {
    const { showTitle, label, as: titleAs, className } = props;
    const TitleComponent = (titleAs || "div") as "div";
    if (!showTitle || !label) return null;
    return (
      <TitleComponent
        key="title-node"
        className={clsx("flex-title", className)}
      >
        <RenderHtml>{label}</RenderHtml>
      </TitleComponent>
    );
  },
);

function DefaultFlexRow<T extends keyof React.JSX.IntrinsicElements = "div">(
  props: IFlexProps<T>,
) {
  const {
    as: As = "div",
    isInline = true,
    flex,
    title,
    description,
    reverse = false,
    showTitle = true,
    showDescription = true,
    showChildrenBeforeDescription = false,
    titleAs,
    titleClassName,
    descriptionClassName,
    ...elementProps
  } = props;

  const className = clsx(
    CLASS_CONSTANTS.UTILITIES.FLEX,
    getDirection(
      clsx({
        [CLASS_CONSTANTS.UTILITIES.FLEX_ROW]: isInline,
        [CLASS_CONSTANTS.UTILITIES.FLEX_COLUMN]: !isInline,
      }),
      reverse,
    ),
    elementProps.className,
    flexClassName,
  );

  const componentProps = {
    ...elementProps,
    className: clsx(className),
    style: flex
      ? { ...props.style, flex: flex ?? props.style?.flex }
      : props.style,
  } as ComponentProps<T>;

  const El = As as unknown as ComponentType<ComponentProps<T>>;

  const renderedTitle = (
    <Title
      className={clsx("flex-title", titleClassName)}
      label={title as string}
      showTitle={showTitle}
      as={titleAs as string}
    />
  );

  const renderedDescription =
    showDescription && description ? (
      <div
        key="descriptionNode-node"
        className={clsx("flex-description", descriptionClassName)}
      >
        <RenderHtml>{description}</RenderHtml>
      </div>
    ) : null;

  const childrenNode = <Fragment key="childrenNode">{props.children}</Fragment>;

  return (
    <El {...componentProps} id={undefined} data-node-id={componentProps.id}>
      {showChildrenBeforeDescription && childrenNode}
      {renderedTitle}
      {renderedDescription}
      {!showChildrenBeforeDescription && childrenNode}
    </El>
  );
}

const FlexRenderer = getRenderer<IFlexProps>("Flex", DefaultFlexRow);

export const FlexRow = function FlexRow<
  T extends keyof React.JSX.IntrinsicElements = "div",
>(props: IFlexProps<T>) {
  return <FlexRenderer {...(props as IFlexProps)} />;
};

export const FlexColumn = function FlexColumn<
  T extends keyof React.JSX.IntrinsicElements = "div",
>(props: IFlexProps<T>) {
  return (
    <FlexRow
      {...props}
      isInline={
        props.isInline ??
        props.className?.includes(CLASS_CONSTANTS.UTILITIES.FLEX_ROW) ??
        false
      }
    />
  );
};

const flexClassName = css`
  &.${CLASS_CONSTANTS.UTILITIES.FLEX} {
    display: flex;
    flex-wrap: wrap;

    > * {
      max-width: 100%;
    }
  }

  &.${CLASS_CONSTANTS.UTILITIES.FLEX_NOWRAP} {
    flex-wrap: nowrap;
  }

  &.${CLASS_CONSTANTS.UTILITIES.FLEX_ROW} {
  }

  &.${CLASS_CONSTANTS.UTILITIES.FLEX_COLUMN} {
    flex-direction: column;
  }

  &.${getDirection(CLASS_CONSTANTS.UTILITIES.FLEX_ROW, true)} {
    flex-direction: row-reverse;
    justify-content: flex-end;
    align-items: center;
  }

  &.${getDirection(CLASS_CONSTANTS.UTILITIES.FLEX_COLUMN, true)} {
    flex-direction: column-reverse;
    justify-content: flex-end;
  }

  gap: var(--ws-gutter, 0);

  &.${CLASS_CONSTANTS.SEMANTIC.TWO_COLUMN} {
    align-items: flex-start !important;
    gap: var(--ws-gutter, 1em);

    .${CLASS_CONSTANTS.SEMANTIC.LEFT_COLUMN}, .${CLASS_CONSTANTS.SEMANTIC.RIGHT_COLUMN} {
      align-self: stretch;

      &:only-child {
        flex-basis: auto;
        padding: 0;
        max-width: none;
      }
    }

    @container ws-widget (max-width: 787px) {
      flex-wrap: wrap;
      .${CLASS_CONSTANTS.SEMANTIC.LEFT_COLUMN}, .${CLASS_CONSTANTS.SEMANTIC.RIGHT_COLUMN} {
        width: 100%;
      }
    }

    @container ws-widget (width > 787px) {
      .${CLASS_CONSTANTS.SEMANTIC.LEFT_COLUMN} {
        --ws-left: clamp(30%, var(--ws-left-cell-width), 100%);
        --ws-width: calc(var(--ws-left, 50%) - calc(var(--ws-gutter, 0) / 2));
        flex-basis: var(--ws-width, 50%);
        width: var(--ws-width);
      }

      .${CLASS_CONSTANTS.SEMANTIC.RIGHT_COLUMN} {
        --ws-right: clamp(30%, calc(100% - var(--ws-left-cell-width)), 100%);
        --ws-width: calc(var(--ws-right, 50%) - calc(var(--ws-gutter, 0) / 2));
        flex-basis: var(--ws-width, 50%);
        width: var(--ws-width);
      }
    }
  }
`;
